<template>
  <div>
    <v-stepper-vertical flat color="primary" :model-value="steps">
      <template #default>
        <v-stepper-vertical-item
          :complete="true"
          :title="t('swanOnboarding.emailInvitationSent')"
          value="1"
        >
          <div class="text-sm">
            {{ t("swanOnboarding.emailSentToContact") }}
          </div>

          <div class="mt-3">
            <v-btn
              color="primary"
              variant="tonal"
              :loading="loading"
              :disabled="loading"
              @click="resendLink"
              >{{ t("swanOnboarding.resend") }}</v-btn
            >
            <v-btn class="ml-3" :to="`/account/${accountId}/settings`" variant="plain">{{
              t("swanOnboarding.manageContacts")
            }}</v-btn>
          </div>

          <template #next> </template>
          <template #prev> </template>
        </v-stepper-vertical-item>

        <v-stepper-vertical-item
          :complete="account?.swan_onboarding?.status === 'Finalized'"
          :title="t('swanOnboarding.companyInfoCollection')"
          value="2"
        >
          <div class="text-sm">
            {{ t("swanOnboarding.companyInfoInProgress") }}
            <v-tooltip>
              <template #activator="{ props: _props }">
                <v-icon v-bind="_props" color="#9E9E9E">mdi-information</v-icon>
              </template>
              <span>{{ account?.swan_onboarding?.status }}</span>
            </v-tooltip>
          </div>
          <AdminOnlyCard v-if="adminForm" class="mt-3 text-sm">
            <div>
              {{ t(`swanKyb.form.${adminForm.state}`) }}
              <v-tooltip>
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" color="#9E9E9E" size="small"
                    >mdi-information</v-icon
                  >
                </template>
                <div>{{ t("swanKyb.form.rawStatus", { status: adminForm.status }) }}</div>
                <div v-for="error in adminForm.errors" :key="error.field">
                  {{ `${error.field} (${error.codes.join(", ")})` }}
                </div>
              </v-tooltip>
            </div>
            <div v-if="adminForm.state === 'blocked'" class="mt-1">
              {{ t("swanKyb.form.blockedFields") }}
              <ul class="list-disc ml-6">
                <li v-for="field in adminForm.blockedFields" :key="field">
                  {{ t(`swanKyb.fields.${field}`) }}
                </li>
              </ul>
            </div>
          </AdminOnlyCard>
          <template #next></template>
          <template #prev></template>
        </v-stepper-vertical-item>

        <v-stepper-vertical-item
          :complete="account?.swan_account_holder?.verification_status === 'Verified'"
          :title="t('swanOnboarding.accountHolderVerification')"
          value="3"
        >
          <div class="text-sm">
            {{ t("swanOnboarding.verificationRequired") }}
          </div>
          <template #next></template>
          <template #prev></template>
        </v-stepper-vertical-item>
        <v-stepper-vertical-item
          :complete="!!account?.swan_account?.IBAN"
          :title="t('swanOnboarding.ibanDelivered')"
          value="4"
        >
          <div class="text-sm">{{ t("swanOnboarding.waitingForIban") }}</div>
          <template #next> </template>
          <template #prev> </template>
        </v-stepper-vertical-item>
      </template>
    </v-stepper-vertical>
  </div>
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import type { Account } from "@aircap/common/account";
import { getSwanKybFormState } from "@aircap/common/swan";
import { captureException } from "@sentry/vue";
import { collection, doc } from "firebase/firestore";
import { computed, ref } from "vue";
import { VStepperVertical, VStepperVerticalItem } from "vuetify/components/VStepperVertical";

const props = defineProps<{
  accountId: string;
}>();

const loading = ref(false);
const { showSuccessToast, showErrorToast } = useToast();
const { t } = useI18n();

const accountSource = computed(() => doc(collection<Account>(db, "accounts"), props.accountId));
const { data: account } = useDocument<Account>(accountSource);

const resendLink = async () => {
  loading.value = true;
  try {
    console.log("Resending Swan onboarding link...");
    await call("sendSwanOnboardingLink")({ accountId: props.accountId });
    showSuccessToast(t("swanOnboarding.emailSentSuccess"));
  } catch (cause) {
    const error = new Error("Error sending onboarding link", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("swanOnboarding.emailSendError"));
  } finally {
    loading.value = false;
  }
};

const adminForm = computed(() => {
  const kyb = account.value?.swan_kyb;
  if (!kyb) return undefined;
  const state = getSwanKybFormState(kyb.form);
  if (state === "finished") return undefined;
  return {
    ...kyb.form,
    state,
    blockedFields: [
      ...new Set(
        kyb.form.errors
          .filter(({ codes }) => codes.includes("Invalid"))
          .map(({ category }) => category),
      ),
    ],
  };
});

const steps = computed(() => {
  if (!account.value?.swan_onboarding) return [];

  if (account.value.swan_onboarding?.status !== "Finalized") return [1, 2];
  else if (account.value.swan_account_holder?.verification_status !== "Verified") return [3];
  else return [4];
});
</script>
